import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatPrice } from "../../lib/format";
import { ProductPurchaseActions } from "./ProductPurchaseActions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

/**
 * PDP right-column action block — price card, purchase actions, Request-
 * Availability dialog, RUO banner, and trust rows. Structural extraction
 * from ProductDetail.jsx.
 */
export function ProductActions({ product, canAdd, showPrice, navigate }) {
  const [qty, setQty] = useState(1);
  const [reqOpen, setReqOpen] = useState(false);

  const displayPrice = showPrice ? formatPrice(product.retail_price_cents) : "Pricing on approval";

  return (
    <>
      <div className="mps-card p-6 lg:p-7">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted mb-2">
          Price
        </div>
        <div
          className={
            showPrice
              ? "font-display text-4xl font-bold text-brand-navy tracking-tight"
              : "text-sm font-semibold uppercase tracking-[0.14em] text-brand-muted"
          }
          data-testid="product-price"
        >
          {displayPrice}
        </div>
        <div className="mt-2 text-xs text-brand-muted">
          {canAdd
            ? "USD, exclusive of shipping and taxes."
            : "Availability and pricing require account verification and product approval."}
        </div>

        <ProductPurchaseActions
          product={product}
          variant="detail"
          quantity={qty}
          onIncrement={() => setQty((q) => q + 1)}
          onDecrement={() => setQty((q) => Math.max(1, q - 1))}
          onOpenRequest={() => setReqOpen(true)}
          navigate={navigate}
        />

        {/* Made-in-USA ribbon — sits directly under the ATC actions so
            it lands in the same eye-line as the "Add to Cart" moment.
            Kept as one accented row (not part of the 3-item proof grid
            below) so the primary trust anchor never has to fight for
            space with the operational chips. */}
        {canAdd && (
          <div
            data-testid="pdp-made-in-usa"
            className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-accent/10 border border-brand-accent/25 text-[12px] font-semibold text-brand-navy"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" aria-hidden />
            Peptides made in the USA
          </div>
        )}

        {!canAdd && (
          <Dialog open={reqOpen} onOpenChange={setReqOpen}>
            <DialogContent className="bg-white border-brand-border text-brand-text max-w-lg rounded-[20px]">
              <DialogHeader>
                <DialogTitle className="font-display text-brand-navy">
                  Contact for Availability
                </DialogTitle>
                <DialogDescription className="text-sm text-brand-muted">
                  {product.public_display_name}
                </DialogDescription>
              </DialogHeader>
              <RequestAvailabilityForm product={product} onDone={() => setReqOpen(false)} />
            </DialogContent>
          </Dialog>
        )}

        {/* Compact conversion-proof strip — three supported facts that
            most often decide "buy vs. tab-close" at price glance.  Every
            string is defensible from checkout + policy.  Rendered inside
            the price card so shoppers see it before they scroll. */}
        {canAdd && (
          <ul
            data-testid="pdp-price-proof-strip"
            className="mt-6 pt-5 border-t border-brand-border grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px] text-brand-muted"
          >
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent shrink-0" aria-hidden />
              Live carrier rates at checkout
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent shrink-0" aria-hidden />
              Signature required on delivery
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent shrink-0" aria-hidden />
              Batch documents on request
            </li>
          </ul>
        )}
      </div>

      {/* RUO banner */}
      <div className="rounded-[14px] border border-amber-200 bg-amber-50 p-4" data-testid="pdp-research-banner">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-800 mb-2">
          Regulatory Notice
        </div>
        <ul className="text-[13px] text-amber-900 space-y-0.5 leading-relaxed">
          <li>For research use only</li>
          <li>Not for human or veterinary use</li>
          <li>Not intended to diagnose, treat, cure or prevent any disease</li>
        </ul>
      </div>

      {/* Trust rows */}
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-3 text-brand-text">
          <Truck size={18} weight="regular" className="text-brand-accent mt-0.5 shrink-0" />
          <span>Live carrier rates. Signature required at delivery.</span>
        </div>
        <div className="flex items-start gap-3 text-brand-muted">
          <ShieldCheck size={18} weight="regular" className="text-brand-accent mt-0.5 shrink-0" />
          <span>
            All sales are final except where required by law.{" "}
            <Link to="/returns" className="text-brand-accent hover:underline font-semibold">
              Claims policy
            </Link>
            .
          </span>
        </div>
      </div>
    </>
  );
}

function RequestAvailabilityForm({ product, onDone }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Backwards-compat: the API still accepts organization/account_type/intended_use
      // for admin auditing.  We send neutral defaults so the public form
      // never asks for institutional / professional / wholesale context.
      await api.post("/availability-requests", {
        product_id: product.id,
        ...form,
        organization: "",
        account_type: "individual_catalog_user",
        intended_use: "Customer requested notification when this item is back in stock.",
      });
      toast.success("Request received. Our team will follow up shortly.");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="request-availability-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Your name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="req-name" />
        </div>
        <div>
          <Label>Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="req-email" />
        </div>
      </div>
      <div>
        <Label>Message (optional)</Label>
        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="req-message" placeholder="Anything we should know — for example, quantity or timing." />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="req-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold px-6 py-3.5 text-base rounded-[14px] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft disabled:opacity-50 disabled:hover:transform-none"
      >
        {busy ? "Submitting…" : "Notify me"}
      </button>
    </form>
  );
}
